import type { AxiosResponse } from 'axios';
import {
  Database,
  Calendar,
  ListOrdered,
  Settings,
  BarChart3,
  Home,
} from 'lucide-react';

import type { IUserProfile } from '@/Shared/UserProfile/user.api';
import api from '@/utils/axiosInterceptor.ts';
import Premission from '@/utils/premissions/premission.type.ts';

export interface IItemList {
  ID: number;
  Name: string;
  Price: number;
  inGroup: boolean;
  groupLength: number;
}

interface ICountDetailCard {
  Name: string;
  ExpireDate: Date;
  BNO: string;
  Item_Count: number;
}

export interface IItemCard {
  ID: number;
  Code: string;
  Name: string;
  NameE: string;
  Group_ID: number;
  Price: number;
  DiscountIN: number;
  DiscountOut: number;
  DemandLimit: number;
  ItemClassName: string;
  CompanyName: string;
  Company_ID: number;
  Notes: string;
  DiscountPharmacy: number;
  DiscountStore: number;
  DiscountAgent: number;
  Discount5: number;
  Discount6: number;
  ISTabled: boolean;
  MaxSale: number;
  Enabled: boolean;
  BuyPercentUpdateMethod: number;
  UpdatePrice: boolean;
  PriceInAvg: number;
  Rare: boolean;
  RatioSalesLower: number;
  RatioSalesUpper: number;
  EnabledSales: boolean;
  ImportantItem: boolean;
  HasExpired: boolean;
  UpdateSalesRatio: boolean;
  Discount7: number;
  IsSpecial: boolean;
  Place_Id: number;
  PlaceName: string;
  IsBulk: boolean;
  IsImported: boolean;
  IsAcc: boolean;
  SmallPartCount: number;
  SalesTax: number;
  DiscPro: number;
  TaxStore: number;
  TaxAgent: number;
  TaxPharmacy: number;
  TaxOut: number;
  Tax5: number;
  Tax6: number;
  Tax7: number;
  SaledComplete: boolean;
  MinStock: number;
  MinStockToZero: number;
  IsRefItem: boolean;
  IsExpensive: boolean;
  SectorName: string;
  Sector_ID: number;
  count: number;
  countDetails: ICountDetailCard[];
  _id: string;
  itemID: number;
  itemLogs: string[]; // Adjust type as needed
  itemPapers: string[]; // Adjust type as needed
  avatar: string | null;
  createdAt: Date;
  updatedAt: Date;
}
export interface IItemsTotals {
  SalesRetail: number;
  SalesWholeSale: number;
  RC_Retail: number;
  RC_WholeSale: number;
  IncomeIn: number;
  RS_In: number;
}
export interface IOfferItemsLast {
  itemID: number;
  itemName: string;
  itemPrice: number;
  _id: string;
  ClientID: string;
  OfferHeadID: string;
  ClientItemName: string;
  HealthyItemID: number;
  ClientItemID: string | null;
  Price: number;
  Discount: number;
  UploadUser: IUserProfile;
  UploadDate: string;
  IsLastUpdate: boolean;
  IsLastOffer: boolean;
  checkPrice: boolean;
  clientOid: string;
  clientID: number;
  clientName: string;
  suppliarClass: string;
}

export interface ItemCardResponse {
  itemsLength: number;
  items: IItemCard[];
  GID: string;
  allOffers: IOfferItemsLast[];
}

export type SubLists = {
  ID: number;
  Name: string;
};

export type IBooleanFilter = {
  isUse: boolean;
  value: string;
  label: string;
};

export type IArrayFilter = {
  isUse: boolean;
  value: SubLists[];
  label: string;
};

export type IRangedFilter = {
  isUse: boolean;
  minValue: number;
  maxValue: number;
  label: string;
};

export type BooleanFilter = {
  Enabled: IBooleanFilter;
  ISTabled: IBooleanFilter;
  Rare: IBooleanFilter;
};

export type RangeFilter = {
  Price: IRangedFilter;
  DiscountIN: IRangedFilter;
};

export type ArrayFilter = {
  Company_ID: IArrayFilter;
  Group_ID: IArrayFilter;
  Sector_ID: IArrayFilter;
};

export type IFilterItem = {
  booleanFilter: BooleanFilter;
  rangeFilter: RangeFilter;
  arrayFilter: ArrayFilter;
};
export interface IDiscountType {
  ID: number;
  Name: string;
  PayRatio: number;
  DiscountPro: number;
  Notes: string;
  DiscountPro2: number;
  title:
    | 'DiscountOut'
    | 'DiscountAgent'
    | 'DiscountPharmacy'
    | 'DiscountStore'
    | 'Discount5'
    | 'Discount6'
    | 'Discount7';
  priority: number;
}
export interface IGetItemsList {
  items: IItemList[];
  total: number;
  companies: SubLists[];
  discountTypes: IDiscountType[];
  easyItemsGroup: SubLists[];
}

export interface IItemsConstants {
  companies: SubLists[];
  groupItems: SubLists[];
  discountTypes: IDiscountType[];
  sectors: SubLists[];
}

export const initialFilterItem: IFilterItem = {
  booleanFilter: {
    Enabled: {
      isUse: false,
      value: '',
      label: 'عامل',
    },
    ISTabled: {
      isUse: false,
      value: '',
      label: 'جدول',
    },
    Rare: {
      isUse: false,
      value: '',
      label: 'نواقص',
    },
  },
  rangeFilter: {
    Price: {
      isUse: false,
      minValue: 0,
      maxValue: 0,
      label: 'السعر',
    },
    DiscountIN: {
      isUse: false,
      minValue: 0,
      maxValue: 0,
      label: 'خصم الشراء',
    },
  },
  arrayFilter: {
    Company_ID: {
      isUse: false,
      value: [],
      label: 'الشركات المنتجة',
    },
    Group_ID: {
      isUse: false,
      value: [],
      label: 'مجموعات الاصناف',
    },
    Sector_ID: {
      isUse: false,
      value: [],
      label: 'القطاعات',
    },
  },
};

export const ItemCardTabs = [
  {
    label: 'عام',
    value: 'mainData',
    icon: <Home />,
    Prem: Premission.Prem_Items_Show_Full_Summary,
  },
  {
    label: ' بيانات ',
    value: 'easyData',
    icon: <Settings />,
    Prem: Premission.Prem_Items_Open_Page,
  },
  {
    label: 'اجماليات ',
    value: 'totals',
    icon: <BarChart3 />,
    Prem: Premission.Prem_Items_Show_Items_Total,
  },
  {
    label: 'عروض ',
    value: 'offers',
    icon: <BarChart3 />,
    Prem: Premission.Prem_Items_Show_Full_Summary,
  },

  {
    label: 'حركات',
    value: 'transactions',
    icon: <ListOrdered />,
    Prem: Premission.Prem_Items_Show_Items_Trans,
  },
  {
    label: 'ملفات ',
    value: 'files',
    icon: <Database />,
    Prem: Premission.Prem_Items_Files,
  },
  {
    label: 'تعديلات',
    value: 'edits',
    icon: <Calendar />,
    Prem: Premission.Prem_Items_Edit_Logs,
  },
];

export interface IItemsBooleanFields {
  ImportantItem: boolean;
  HasExpired: boolean;
  UpdateSalesRatio: boolean;
  IsSpecial: boolean;
  IsImported: boolean;
  IsAcc: boolean;
  IsBulk: boolean;
  IsRefItem: boolean;
  IsExpensive: boolean;
  ISTabled: boolean;
  Rare: boolean;
  UpdatePrice: boolean;
  SaledComplete: boolean;
  Enabled: boolean;
}

export interface ILogItem {
  _id: string;
  ItemID: number;
  Item: string;
  userID: {
    _id: string;
    empName: string;
    avatar: string;
  };
  actionDetails: string;
  createdAt: string;
}

export interface IItemTotals {
  SalesRetail: number;
  SalesWholeSale: number;
  SalesOut: number;
  RC_Retail: number;
  RC_WholeSale: number;
  RC_Out: number;
  IncomeOut: number;
  IncomeIn: number;
  RS_In: number;
  RS_Out: number;
  startDate: string;
  endDate: string;
}

export interface ISalesTrans {
  ID: number;
  Customer_ID: number;
  ClientName: string;
  ClassName: string;
  Qty: number;
  BNO: string;
  ExpireDate: string;
  Discount: number;
  Item_ID: number;
  ItemName: string;
  Price: number;
  UnitPrice: number;
  TrDate: string;
  TrTime: string;
  EmpName: string;
  EmpID: number;
}

export interface IIncomeTrans {
  ID: number;
  Supplier_ID: number;
  ClientName: string;
  RegionName: string;
  Qty: number;
  BNO: string;
  Bonus: number;
  TrDate: string;
  TrTime: string;
  EmpName: string;
  DiscountPro: number;
  Tax: number;
  EmpID: number;
  BuyPrice: number;
  ExpireDate: string;
  Discount: number;
  Item_ID: number;
  ItemName: string;
  UnitPrice: number;
  Price: number;
}

export interface ILabelValue {
  label: string;
  value: number;
}

export interface ITransactionItem {
  ID: string;
  Master_ID: number;
  Item_ID: number;
  Name: string;
  ClientName?: string;
  ClientID?: number;
  InFlag: number;
  Qty: number;
  TrDate: string;
  Discount: number;
  UnitPrice: number;
  Price: number;
  Tax: number;
  Bonus: number;
  BNO: string;
  ExpireDate: string;
  FDiscount: string;
  EmployeeName: string;
  RegionID?: number;
  RegionName?: string;
  StoreID: number;
  StoreName: string;
  type: string;
  OldCount?: number;
  NewCount?: number;
}

export interface ISummary {
  total: number;
  transactionCounts: number;
  clientsCount?: number;
  suppliersCount?: number;
}

export interface ITransFullData {
  sales: ISummary;
  IN: ISummary & { suppliersCount: number };
  RC: ISummary & { clientsCount: number };
  RS: ISummary & { suppliersCount: number };
  InterChange: ISummary;
  EditCount: ISummary;
  Damage: ISummary;
  lastTransactionDate: string;
  total: number;
  transactions: ITransactionItem[];
  startDate: string;
  endDate: string;
}

export interface IItemOffer {
  _id: string;
  Price: number;
  ClientID: string;
  ClientItemName: string;
  Discount: number;
  HealthyItemID: number;
  IsLastOffer: boolean;
  IsLastUpdate: boolean;
  OfferHeadID: string;
  OldUserName: string;
  UploadDate: string;
  UploadUser: {
    _id: string;
    avatar: string;
    empName: string;
  };
  ClientItemID: number | null;
  clientOid: string;
  clientID: number;
  clientName: string;
  suppliarClass: string;
  checkPrice: boolean;
}

export interface IItemOfferTiny {
  _id: string;
  Price: number;
  __v: number;
  ClientID: string;
  ClientItemID?: number | null;
  ClientItemName: string;
  Discount: number;
  HealthyItemID: number;
  IsLastOffer: boolean;
  IsLastUpdate: true;
  OfferHeadID: string;
  OldUserName: string;
  UploadDate: string;
  UploadUser: {
    _id: string;
    avatar: string;
    empName: string;
  };
}

export interface IItemFull {
  itemID: number;
  itemName: string;
  itemPrice: number;
  itemOffersLength: number;
  itemOffers: IItemOffer[];
}

export interface ItemPerDate {
  date: string;
  count: number;
  maxValue: number;
  minValue: number;
  clients: string[];
}

export interface ItemWithOffersByDate {
  itemID: number;
  itemName: string;
  itemPrice: number;
  itemsPerDate: ItemPerDate[];
  allOffersCount: number;
}

export interface IItemSearchResponse {
  Items: IItemFull[];
  itemsData: {
    itemID: number;
    itemName: string;
    itemPrice: number;
  }[];
  ItemsLength: number;
  ItemOffersGroupByDate: ItemWithOffersByDate[];
}

export interface IClientItemHistory {
  client: {
    clientID: number;
    clientOid: string;
    clientName: string;
    clientClass: string;
  };
  item: {
    ID: number;
    Name: string;
    Price: number;
  };
  allOffers: IItemOfferTiny[];
}
export interface IItemOfferPerDay {
  _id: string;
  Price: number;
  __v: number;
  ClientID: {
    _id: string;
    ID: number;
    suppliarClass: string;
  };
  ClientItemID: number;
  ClientItemName: string;
  Discount: number;
  HealthyItemID: number;
  IsLastOffer: boolean;
  IsLastUpdate: boolean;
  OfferHeadID: string;
  OldUserName: string;
  UploadDate: string;
  UploadUser: {
    _id: string;
    avatar: string;
    empName: string;
  };
  clientID: number;
  clientName: string;
  clientClass: string;
  checkPrice: number;
}

export interface IResponseItemPerDay {
  item: {
    ID: number;
    Name: string;
    Price: number;
  };
  allOffers: IItemOfferPerDay[];
}

export async function GetItemsListQueryApi(query?: string) {
  try {
    const response: AxiosResponse<IGetItemsList> = await api.get(
      `/items-card/items-list${query ? `?${query}` : ''}`,
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function GetItemsConstantsApi() {
  try {
    const response: AxiosResponse<IItemsConstants> = await api.get(
      '/items-card/item-constants',
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function GetOneItemApi(id: number | null) {
  try {
    const response: AxiosResponse<ItemCardResponse> = await api.get(
      `/items-card/item/${id}`,
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function GetOneItemTinySummaryApi(id: number | null) {
  try {
    const response: AxiosResponse<IItemsTotals> = await api.get(
      `/items-card/item-summary-tiny/${id}`,
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function UpdateItemsCacheApi() {
  try {
    const response: AxiosResponse = await api.get('/items-card/reset-cache');
    return response?.data;
  } catch (err) {
    throw err;
  }
}

// api for update boolean fields
export async function UpdateBooleanValuesApi({
  itemID,
  data,
}: {
  itemID: number;
  data: IItemsBooleanFields;
}) {
  try {
    const response: AxiosResponse = await api.patch(
      `/items-card/item-update-boolean/${itemID}`,
      data,
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

// api for get items logs
export async function GetItemLogsApi(id: number): Promise<ILogItem[]> {
  try {
    const response: AxiosResponse = await api.get(
      `/items-card/item-logs/${id}`,
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function GetOneItemTransApi(
  id: number,
  startDate: string,
  endDate: string,
): Promise<ITransFullData> {
  try {
    const response: AxiosResponse = await api.get(
      `/items-card/item-trans?id=${id}&startDate=${startDate}&endDate=${endDate}`,
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function GetOneItemTotalsApi(
  id: number,
  startDate: string,
  endDate: string,
): Promise<IItemTotals> {
  try {
    const response: AxiosResponse = await api.get(
      `/items-card/item-totals?id=${id}&startDate=${startDate}&endDate=${endDate}`,
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function GetOneItemSalesApi(
  id: number,
  startDate: string,
  endDate: string,
  type: number,
): Promise<ISalesTrans[]> {
  try {
    const response: AxiosResponse = await api.get(
      `/items-card/item-sales?id=${id}&startDate=${startDate}&endDate=${endDate}&type=${type}`,
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function GetOneItemRCApi(
  id: number,
  startDate: string,
  endDate: string,
  type: number,
): Promise<ISalesTrans[]> {
  try {
    const response: AxiosResponse = await api.get(
      `/items-card/item-rc?id=${id}&startDate=${startDate}&endDate=${endDate}&type=${type}`,
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function GetOneItemIncomeApi(
  id: number,
  startDate: string,
  endDate: string,
  type: number,
): Promise<IIncomeTrans[]> {
  try {
    const response: AxiosResponse = await api.get(
      `/items-card/item-income?id=${id}&startDate=${startDate}&endDate=${endDate}&type=${type}`,
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function GetOneItemRSApi(
  id: number,
  startDate: string,
  endDate: string,
  type: number,
): Promise<IIncomeTrans[]> {
  try {
    const response: AxiosResponse = await api.get(
      `/items-card/item-rs?id=${id}&startDate=${startDate}&endDate=${endDate}&type=${type}`,
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function GetOneItemSalesMonthApi(
  id: number,
  type: number,
): Promise<ILabelValue[]> {
  try {
    const response: AxiosResponse = await api.get(
      `/items-card/item-sales-month?id=${id}&type=${type}`,
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function GetOneItemIncomeMonthApi(
  id: number,
  type: number,
): Promise<ILabelValue[]> {
  try {
    const response: AxiosResponse = await api.get(
      `/items-card/item-income-month?id=${id}&type=${type}`,
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function UpdateItemAvatarApi({
  id,
  data,
}: {
  id: string;
  data: FormData;
}) {
  const response = await api.post(`/item-upload/avatar/${id}`, data);
  return response?.data;
}

export async function UpdateItemPaperApi({
  id,
  data,
}: {
  id: string;
  data: FormData;
}) {
  const response = await api.patch(`/item-upload/papers/${id}`, data);
  return response?.data;
}

export async function DeleteItemPaperApi(paperData: {
  id: string;
  papers: string[];
}) {
  const response = await api.patch(`/item-upload/papers-pull/`, paperData);
  return response?.data;
}

export async function SearchOneItem(id: number): Promise<IItemSearchResponse> {
  const response = await api.get(`/offer/search-item-offers/${id}`);
  return response.data;
}

export async function SearchOneItemClientHistory(
  id: number,
  client: string,
): Promise<IClientItemHistory> {
  const response = await api.get(
    `/offer/one-item-client?id=${id}&client=${client}`,
  );
  return response.data;
}

export async function SearchOneItemDayHistory(
  id: number,
  date: string,
): Promise<IResponseItemPerDay> {
  const response = await api.get(
    `/offer/get-day-offers-item?itemID=${id}&date=${date}`,
  );
  return response.data;
}

export async function SearchOneItemQuarterHistory(
  id: number,
): Promise<IResponseItemPerDay> {
  const response = await api.get(`/offer/get-quarter-offers-item?id=${id}`);
  return response.data;
}
